import { Rating } from './rating';
import { RssItem } from './rss';

export interface Record {
  id: string;
  active?: boolean;
  title?: string;
  subtitle?: string;
  author?: string;
  authorFollowedByActiveUser?: boolean;
  container?: boolean;
  containerData?: ContainerDataDTO;
  selectedRssItem?: RssItem;
  isbn?: string;
  description?: string;
  coverImageUrl?: string;
  originalImageUrl?: string;
  formats?: Format[];
  resourceTypes?: string[];
  gradeLevel?: string;
  language?: string;
  publisher?: string;
  subject?: string;
  subjects?: string[];
  narrators?: string[];
  availableIssueDates?: string[];
  totalCheckouts?: number;
  totalViews?: number;
  externalLink?: string;
  specialFormat?: string;
  publicationDate?: string;
  attachedResource?: AttachedResource;
  availability?: RecordAvailability;
  icon?: Icon;
  series?: string;
  seriesPosition?: string;
  hasPreview?: boolean;
  imageLoaded?: boolean;
  requestedBy?: number;
  inLibrary?: boolean;
  fileType?: string;
  fileSize?: string;
  electronicLocation?: ElectronicLocation[];
  fromBookClub?: boolean;
  payPerCheckout?: boolean;
  ppulicenseComplete?: boolean;
  themata?: string[];
  learningExperienceId?: string;
  iconId?: string;
  associatedExperiences?: AssociatedExperience[];
  rating?: Rating;
  citable?: boolean;
  physicalFormat?: string;
  typeAction?: string;
  wishListInfo?: RecordWishListInfo;
  collection?: string;
  collectionToShow?: string;
  fileFormat?: string;
  entities?: string;
  production?: string;

  metadata?: Metadata[];
  accessibilityMetadata?: Metadata[];

  freeDownload?: boolean;
}

export interface ActiveRecord extends Record {
  collectionId: number;
  sliderIndex: number;
}

export interface ContainerDataDTO {
  containerType: ContainerType;
  lastRssItems: RssItem[];
  totalItems: number;
}

export enum ContainerType {
  RSS = 'RSS',
}

export interface RecordSuggestion {
  recordId: string;
  label: string;
  filter: string;
  format?: string;
}

export interface AttachedResource {
  fileName: string;
  mode: AttachedResourceMode;
}

export type AttachedResourceMode = 'OPEN' | 'LOGGED_USERS';

export type Format =
  | 'EBOOK_STREAMING'
  | 'EPUB'
  | 'PDF'
  | 'ACSM'
  | 'ACSM_PDF'
  | 'ACSM_EPUB'
  | 'HTML'
  | 'MP3'
  | 'MP3_LH'
  | 'MP4'
  | 'TXT'
  | 'DE_PDF'
  | 'MOBI'
  | 'WMA'
  | 'WMV'
  | 'DAISY'
  | 'PLKR'
  | 'QIOO, JPG, MP4'
  | 'SCORM'
  | 'OCS'
  | 'CB_DOWNLOAD'
  | 'UNKNOWN'
  | 'AUDIO_ENCRYPTED'
  | 'VIDEO_ENCRYPTED';

export const hiddenFormats: Format[] = ['AUDIO_ENCRYPTED', 'VIDEO_ENCRYPTED', 'OCS', 'CB_DOWNLOAD'];
export const streamingFormats: Format[] = ['MP3', 'MP4'];

export enum IconFormat {
  FORMAT_ALL = 'view_1',
  FORMAT_MUSIC = 'format_music',
  FORMAT_NEWSPAPER = 'format_newspaper',
  FORMAT_VIDEO = 'format_video',
  FORMAT_AUDIO = 'format_audio',
  FORMAT_MAGAZINE = 'format_magazine',
  FORMAT_EBOOK = 'format_ebook',
  FORMAT_IMAGE = 'format_image',
  FORMAT_PODCAST = 'format_podcast',
  FORMAT_ARTICLE = 'format_article',
  FORMAT_SUMMARY = 'format_summary',
  FORMAT_DATABASE = 'format_database',
  FORMAT_EDUCATION = 'format_education',
  FORMAT_UNDEFINED = 'help',
  FORMAT_EXTERNAL = 'link',
  FORMAT_DOWNLOAD = 'download',
  FORMAT_BOOKCLUB = 'le_06',
  FORMAT_LEARNING_CLUB = 'le_09',
  FORMAT_CLASSROOM = 'le_07',
  FORMAT_READING_PLAN = 'le_08',
  FORMAT_LEARNING_PATH = 'le_10',
  FORMAT_UNKNOWN = '',
  FORMAT_MUSIC_SHEET = 'format_sheet_music',
  FORMAT_FILM = 'format_movie',
  FORMAT_ALBUM = 'format_music_album',
  FORMAT_LEARNING_PILL = 'format_learning_pill',
  FORMAT_PHYSICAL = 'format_02_book',
  FORMAT_VACIADO_ARTICULOS = 'format_02_magazine',
  FORMAT_PUBLICACIONES_SERIADAS = 'format_02_magazine',
  FORMAT_FICHEROS_INFORMATICOS = 'format_02_software',
  FORMAT_MAPAS = 'format_02_map',
  FORMAT_MATERIALES_VISUALES = 'format_02_video',
  FORMAT_MUSICA1 = 'format_02_music',
  FORMAT_MUSICA2 = 'format_02_music',
  FORMAT_RECURSOS_CONTINUOS = 'format_02_book',
  FORMAT_ARTICULOS = 'format_02_book',
  FORMAT_CLASIFICACION = 'format_02_book',
  FORMAT_COMUNIDAD = 'format_02_book',
  FORMAT_BK_MATERIAL_IMPRESO_TEXTUAL = 'format_02_book',
  FORMAT_CR_EJEMPLARES = 'format_02_book',
  FORMAT_BK_MATERIAL_MANUSCRITO_TEXTUAL = 'format_02_book',
  FORMAT_MP_MATERIAL_CARTOGRAFICO = 'format_02_book',
  FORMAT_MP_MATERIAL_CARTOGRAFICO_MANUSCRITO = 'format_02_book',
  FORMAT_MU_MUSICA_IMPRESA_CON_NOTACION = 'format_02_book',
  FORMAT_MU_MUSICA_MANUSCRITA = 'format_02_book',
  FORMAT_MU_GRABACION_SONORA_NO_MUSICAL = 'format_02_book',
  FORMAT_VM_MEDIO_PROYECTABLE = 'format_02_book',
  FORMAT_LIST = 'list_notebook',
}

export interface RecordAvailability {
  recordId: string;
  totalCopies: number;
  availableCopies: number;
  holdsQueueSize: number;
  estimatedWait: number;
  availableIssueDates: string[];
  availableToCheckout: boolean;
}

export interface Icon {
  icon: IconFormat | string;
  label: string;
  className?: string;
}

export const podcastIcon: Icon = {
  icon: IconFormat.FORMAT_PODCAST,
  label: 'formats.podcast',
  className: 'odilo-icon',
};

export interface PublisherLink {
  link: string;
  imageUrl: string;
  text: string;
}

export interface RecordSubject {
  term: string;
  label: string;
}

export interface ElectronicLocation {
  uri: string;
  note?: string;
  label?: string;
}
export interface AssociatedExperience {
  recordId: string;
  title: string;
}

export interface RecordWishListInfo {
  id: string;
  privateList: boolean;
  associatedUser: string;
  associatedElements: string[];
  coverIndex: number;
  followers: string[];
  hasCustomCover: boolean;
}

export interface Metadata {
  label: string;
  values: MetadataValue[];
}

export interface MetadataValue {
  text: string;
  queryParams?: string;
  externalLink?: ExternalLink;
}

export interface ExternalLink {
  link: string;
  imageUrl?: string;
}
